import * as d3 from "d3";
import { Typography } from "antd";
import { getDisplayLabel } from "../utils";

type DataValues = Record<string, number>;
type PlotLabel = string | null | undefined;
type CountsData = { name: PlotLabel; values: DataValues };
type DistributionValues = number | { [key: string]: number };
type DistributionData = { name: PlotLabel; values: DistributionValues };

type TablePlotProps =
  | {
      data: CountsData[][];
      mode: "counts";
      preDefinedStacks?: string[];
      stackLabels?: Record<string, string>;
      categoriesTitle?: string;
      sorted?: boolean;
      noTitle?: boolean;
    }
  | {
      data: DistributionData[][];
      mode: "distribution";
      sorted?: boolean;
      preDefinedStacks?: string[];
      stackLabels?: Record<string, string>;
      categoriesTitle?: string;
      noTitle?: boolean;
    };

const { Text } = Typography;
const barColors = ["#D2EFF9", "#64BDE1"];
const rowHeightClass = "h-[35px] min-h-[35px]";
const HIDDEN_STACKS = new Set(["null"]);

const getDistributionValue = (values: DistributionValues) =>
  typeof values === "number" ? values : values.total || 0;

const getValueForStack = (
  values: DataValues | DistributionValues,
  stack: string,
) => {
  if (typeof values === "number") {
    return stack === "total" ? values : 0;
  }

  return (values as Record<string, number>)[stack] || 0;
};

export const TablePlot = ({
  data,
  mode,
  sorted,
  preDefinedStacks,
  stackLabels,
  categoriesTitle,
  noTitle = false,
}: TablePlotProps) => {
  const stacks = preDefinedStacks || getAllUniqueStacks(data);
  const groups = sorted
    ? data.map((items) =>
        [...items].sort(
          (a, b) =>
            getGroupTotal(b.values, stacks) - getGroupTotal(a.values, stacks),
        ),
      )
    : data;

  const barMaxValue =
    mode === "counts"
      ? getCategoryMaxSum(groups as CountsData[][], stacks)
      : getTotalValue(groups as DistributionData[][]);
  const hasCountHeaders = mode === "counts" && !noTitle;
  const gridTemplateColumns = getGridTemplateColumns({
    groups,
    stacks,
    stackLabels,
    mode,
  });

  return (
    <div className="flex h-full w-full flex-col gap-2">
      {hasCountHeaders && (
        <PlotGrid
          gridTemplateColumns={gridTemplateColumns}
          className="items-end gap-x-3 text-[12px] text-[#7F7F7F]"
        >
          <div className="capitalize">{categoriesTitle}</div>
          <div />
          {stacks.map((title) => (
            <div key={title} className="whitespace-nowrap text-end">
              {stackLabels?.[title] ?? title}
            </div>
          ))}
        </PlotGrid>
      )}

      {groups.map((items, groupIndex) => (
        <div
          key={`group-${groupIndex}`}
          className={`flex flex-col gap-1 ${
            groupIndex > 0 ? "border-t border-dashed border-[#E5E5E5] pt-2" : ""
          }`}
        >
          {items.map((item, itemIndex) => {
            const visibleStacks = stacks.filter(
              (stack) => getValueForStack(item.values, stack) > 0,
            );
            const rowMaxValue = barMaxValue[groupIndex] ?? 0;
            const displayLabel = getDisplayLabel(item.name);

            return (
              <PlotGrid
                key={`${groupIndex}-${itemIndex}-${displayLabel}`}
                gridTemplateColumns={gridTemplateColumns}
                className={`items-center gap-x-3 ${rowHeightClass}`}
              >
                <div className="min-w-0 text-[#2B4752]">
                  <Text
                    data-full-text={displayLabel}
                    data-testid="table-plot-label"
                    ellipsis={{ tooltip: true }}
                  >
                    {displayLabel}
                  </Text>
                </div>

                <div className={`${rowHeightClass} flex items-center`}>
                  <div className="relative h-[75%] w-full overflow-hidden rounded-sm">
                    {stacks.map((stack, stackIndex) => {
                      const currentValue = getValueForStack(item.values, stack);
                      const width =
                        rowMaxValue > 0
                          ? (currentValue / rowMaxValue) * 100
                          : 0;
                      const left = getStackOffset({
                        itemValues: item.values,
                        stacks,
                        stackIndex,
                        maxValue: rowMaxValue,
                      });
                      const isFirstVisible = visibleStacks[0] === stack;
                      const isLastVisible =
                        visibleStacks[visibleStacks.length - 1] === stack;

                      if (width <= 0) return null;

                      return (
                        <div
                          key={stack}
                          className={`absolute inset-y-0 ${
                            isFirstVisible ? "rounded-l-sm" : ""
                          } ${isLastVisible ? "rounded-r-sm" : ""}`}
                          style={{
                            left: `${left}%`,
                            width: `${width}%`,
                            backgroundColor:
                              barColors[stackIndex] ??
                              barColors[barColors.length - 1],
                          }}
                        />
                      );
                    })}
                  </div>
                </div>

                {stacks.map((stack) => (
                  <div
                    key={stack}
                    className="whitespace-nowrap text-end font-semibold text-[#2B4752]"
                    data-testid="table-plot-value"
                    data-plot-key={displayLabel}
                    data-plot-stack={stack}
                  >
                    {getValueForStack(item.values, stack)}
                  </div>
                ))}

                {mode === "distribution" && (
                  <div className="whitespace-nowrap text-end font-semibold text-[#2B4752]">
                    {formatPercent({
                      value: getDistributionValue(item.values),
                      total: rowMaxValue,
                    })}
                  </div>
                )}
              </PlotGrid>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const PlotGrid = ({
  children,
  gridTemplateColumns,
  className = "",
}: {
  children: React.ReactNode;
  gridTemplateColumns: string;
  className?: string;
}) => {
  return (
    <div className={`grid w-full ${className}`} style={{ gridTemplateColumns }}>
      {children}
    </div>
  );
};

const formatPercent = ({ value, total }: { value: number; total: number }) => {
  if (total <= 0) return "0%";
  return `${Math.round((value / total) * 100)}%`;
};

const getStackOffset = ({
  itemValues,
  stacks,
  stackIndex,
  maxValue,
}: {
  itemValues: DataValues | DistributionValues;
  stacks: string[];
  stackIndex: number;
  maxValue: number;
}) => {
  if (maxValue <= 0 || stackIndex === 0) return 0;

  return stacks.slice(0, stackIndex).reduce((acc, stack) => {
    const value = getValueForStack(itemValues, stack);
    return acc + (value / maxValue) * 100;
  }, 0);
};

const getGridTemplateColumns = ({
  groups,
  stacks,
  stackLabels,
  mode,
}: {
  groups: TablePlotProps["data"];
  stacks: string[];
  stackLabels?: Record<string, string>;
  mode: TablePlotProps["mode"];
}) => {
  const labelWidth = getLabelColumnWidth(groups);
  const valueWidths = stacks.map((stack) =>
    getValueColumnWidth({
      header: stackLabels?.[stack] ?? stack,
      values: groups.flatMap((items) =>
        items.map((item) => getValueForStack(item.values, stack)),
      ),
    }),
  );
  const distributionWidth =
    mode === "distribution"
      ? getValueColumnWidth({
          header: "%",
          values: groups.flatMap((items) =>
            items.map((item) =>
              formatPercentValue({
                value: getDistributionValue(item.values),
                total: getTotalValue([items])[0] ?? 0,
              }),
            ),
          ),
          extraChars: 1,
        })
      : null;

  return [labelWidth, "minmax(0, 1fr)", ...valueWidths, distributionWidth]
    .filter(Boolean)
    .join(" ");
};

const getLabelColumnWidth = (groups: TablePlotProps["data"]) => {
  const longestLabel = groups
    .flatMap((items) => items.map((item) => getDisplayLabel(item.name).length))
    .reduce((max, length) => Math.max(max, length), 0);

  return `minmax(0, ${Math.max(10, Math.min(longestLabel + 1, 14))}ch)`;
};

const getValueColumnWidth = ({
  header,
  values,
  extraChars = 0,
}: {
  header: string;
  values: Array<number | string>;
  extraChars?: number;
}) => {
  const longestValue = values
    .map((value) => String(value).length)
    .reduce((max, length) => Math.max(max, length), 0);
  const width = Math.max(header.length, longestValue) + extraChars + 1;

  return `${width}ch`;
};

const formatPercentValue = ({
  value,
  total,
}: {
  value: number;
  total: number;
}) => {
  if (total <= 0) return 0;
  return Math.round((value / total) * 100);
};

const getTotalValue = (data: DistributionData[][]) => {
  return data.map((items) =>
    items.reduce((acc, item) => acc + getDistributionValue(item.values), 0),
  );
};

const getGroupTotal = (
  items: DataValues | DistributionValues,
  stacks?: string[],
) => {
  if (typeof items === "number") {
    return items;
  }

  const keys = stacks || Object.keys(items);
  return keys.reduce((acc, key) => acc + getValueForStack(items, key), 0);
};

const getCategoryMaxSum = (
  data: CountsData[][],
  stacks: string[],
): number[] => {
  return data.map(
    (items) => d3.max(items, (item) => getGroupTotal(item.values, stacks)) ?? 0,
  );
};

const getAllUniqueStacks = (
  data: CountsData[][] | DistributionData[][],
): string[] => {
  return Array.from(
    new Set(
      data.flatMap((items) =>
        items.flatMap((item) =>
          typeof item.values === "number"
            ? ["total"]
            : Object.keys(item.values),
        ),
      ),
    ),
  ).filter((stack) => !HIDDEN_STACKS.has(stack));
};
